import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { ICM_LIST_TAKE } from '../icmPaging';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { BusyIcon } from '../../../components/loading';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';

/** One row of legacy Distribution_ListView_Fractional_Consumption. Its column
 *  set is its OWN — Storage / Given To / Total / Remaining / Date belong to the
 *  Distribution and Consumption grids, not this one. */
interface FractionalRow {
  oid: string;
  lt: string | null;
  itemName: string | null;
  specification: string | null;     // shown as "Description"
  amount: string | null;            // Items.Amount, shown as "StockAmount"
  amountLeft: number | null;
  amountTaken: number | null;
  packUnits: string | null;         // Items.Unit -> Packageunits.option, "Units"
  isDeplete: boolean;               // "Depletion"
  lastUpdatedDate: string | null;
  lastUpdatedByName: string | null;
  dateOpened: string | null;
  consumptionByName: string | null; // HISTORY only — "ConsumedBy"
  consumptionDate: string | null;   // HISTORY only — "ConsumedDate"
  statusLabel: string;
}

interface DepletionRow {
  oid: string;
  amountTaken: number;
  stockAmount: number;
  amountRemain: number;
  consumedByName: string | null;
  date: string | null;
  comment: string | null;
  sort: number;
}

/** A depletion row as the GRID holds it. Legacy buffers the whole set in the
 *  child grid and writes nothing until Save — its "New" only adds to the
 *  CollectionSource — so a row added here is `draft` until Save posts the set. */
interface DepEdit extends DepletionRow { draft: boolean }

export default function FractionalConsumptionPage() {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [rows, setRows] = useState<FractionalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Legacy's FractionalHistory action (DistributionViewController ~75) opens
   *  Distribution_ListView_Fractional_Consumption_History — every fractional
   *  record, with ConsumedBy / ConsumedDate in place of LastUpdated*. */
  const [history, setHistory] = useState(false);

  // Detail panel
  const [selectedRow, setSelectedRow] = useState<FractionalRow | null>(null);
  const [depletions, setDepletions] = useState<DepEdit[]>([]);
  const [depDirty, setDepDirty] = useState(false);
  const draftSeq = useRef(0);
  const [depLoading, setDepLoading] = useState(false);
  const [selectedDep, setSelectedDep] = useState<Set<string | number>>(new Set());

  // Add depletion dialog
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/consumptions', { params: { formType: 'fractional', history, take: ICM_LIST_TAKE } });
      setRows((res.data as { items?: FractionalRow[] }).items ?? []);
    } catch {
      setError('Failed to load fractional items.');
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [history]);

  const loadDepletions = useCallback(async (id: string) => {
    setDepLoading(true);
    try {
      const res = await api.get(`/api/v1/fractional-consumptions/${id}`);
      const detail = res.data as { depletions?: DepletionRow[] };
      setDepletions((detail?.depletions ?? []).map(d => ({ ...d, draft: false })));
      setDepDirty(false);
    } catch {
      notify.error('Failed to load depletion records.');
    } finally {
      setDepLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  /** The TOP-BAR title follows the view, as legacy's does. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title',
      { detail: history ? 'Fractional Consumption History' : null }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [history]);

  /** SEARCH-FIRST, as legacy is. DistributionViewController.OnActivated (~93)
   *  puts `Criteria["Filter"] = 1=2` on this view, so the grid opens EMPTY on
   *  purpose, and FullTextFilterAction_Executing (~407) removes that filter when
   *  you search. It is a "find the container in your hand" screen, not a browse
   *  list. HISTORY has no such gate — legacy only applies it to the pending view. */
  const searchGate = !history && search.trim() === '';

  const filtered = useMemo(() => {
    if (searchGate) return [];
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.lt ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.specification ?? '').toLowerCase().includes(t)
    );
  }, [rows, search, searchGate]);

  function selectRow(r: FractionalRow) {
    setSelectedRow(r);
    setSelectedDep(new Set());
    void loadDepletions(r.oid);
  }

  /* The legacy Fractional column set, in its order
     (BTLIMS.Web/Model.xafml Distribution_ListView_Fractional_Consumption):
       LT# . Item . Description . StockAmount . AmountLeft . AmountTaken .
       Units . Depletion . LastUpdatedDate . LastUpdatedBy . DateOpened . Status
     The HISTORY view (…_History) is the same list with ConsumedBy / ConsumedDate
     replacing LastUpdatedDate / LastUpdatedBy.
     Storage, Given To, Total, Remaining and Date are NOT on either — they belong
     to the Distribution and Consumption grids (owner report 2026-09-26). */
  const mainCols: AppColumn<FractionalRow>[] = [
    { field: 'lt',            header: 'LT#',         width: 130, text: r => r.lt ?? '', render: r => r.lt ?? '' },
    { field: 'itemName',      header: 'Item',        width: 200, text: r => r.itemName ?? '', render: r => r.itemName ?? '' },
    { field: 'specification', header: 'Description', width: 220, text: r => r.specification ?? '', render: r => r.specification ?? '' },
    { field: 'amount',        header: 'StockAmount', width: 110, text: r => r.amount ?? '', render: r => r.amount ?? '' },
    { field: 'amountLeft',    header: 'AmountLeft',  width: 110, type: 'number', text: r => String(r.amountLeft ?? 0), value: r => r.amountLeft ?? 0 },
    { field: 'amountTaken',   header: 'AmountTaken', width: 115, type: 'number', text: r => String(r.amountTaken ?? 0), value: r => r.amountTaken ?? 0 },
    { field: 'packUnits',     header: 'Units',       width: 90,  text: r => r.packUnits ?? '', render: r => r.packUnits ?? '' },
    { field: 'isDeplete',     header: 'Depletion',   width: 100, text: r => (r.isDeplete ? 'Yes' : 'No'), render: r => (r.isDeplete ? 'Yes' : 'No') },
    ...(history ? [
      { field: 'consumptionByName', header: 'ConsumedBy',   width: 150,
        text: (r: FractionalRow) => r.consumptionByName ?? '', render: (r: FractionalRow) => r.consumptionByName ?? '' },
      { field: 'consumptionDate',   header: 'ConsumedDate', width: 140,
        text: (r: FractionalRow) => fmtLocalDateTime(r.consumptionDate) ?? '',
        render: (r: FractionalRow) => fmtLocalDateTime(r.consumptionDate) ?? '' },
    ] as AppColumn<FractionalRow>[] : [
      { field: 'lastUpdatedDate',   header: 'LastUpdatedDate', width: 150,
        text: (r: FractionalRow) => fmtLocalDateTime(r.lastUpdatedDate) ?? '',
        render: (r: FractionalRow) => fmtLocalDateTime(r.lastUpdatedDate) ?? '' },
      { field: 'lastUpdatedByName', header: 'LastUpdatedBy',   width: 150,
        text: (r: FractionalRow) => r.lastUpdatedByName ?? '', render: (r: FractionalRow) => r.lastUpdatedByName ?? '' },
    ] as AppColumn<FractionalRow>[]),
    { field: 'dateOpened',    header: 'DateOpened',  width: 130, text: r => fmtLocalDate(r.dateOpened) ?? '', render: r => fmtLocalDate(r.dateOpened) ?? '' },
    { field: 'statusLabel',   header: 'Status',      width: 130, type: 'status', text: r => r.statusLabel },
  ];

  /* The legacy depletion grid (Distribution_itemDepletionsCollection_ListView):
     StockAmount . AmountUnits . AmountTaken . "Amount Left" . ConsumedBy, with
     AmountTaken the ONLY editable cell (every other column is AllowEdit="False"
     there). Units comes off the parent row — the child's AmountUnits FK is the
     same value. */
  const depCols: AppColumn<DepEdit>[] = [
    { field: 'stockAmount',    header: 'StockAmount', width: 110, type: 'number',
      text: r => String(r.stockAmount), value: r => r.stockAmount },
    { field: 'amountUnits',    header: 'AmountUnits', width: 110,
      text: () => selectedRow?.packUnits ?? '', render: () => selectedRow?.packUnits ?? '' },
    { field: 'amountTaken',    header: 'AmountTaken', width: 115, type: 'number',
      edit: { type: 'number' as const },
      text: r => String(r.amountTaken), value: r => r.amountTaken },
    { field: 'amountRemain',   header: 'Amount Left', width: 110, type: 'number',
      text: r => String(r.amountRemain), value: r => r.amountRemain },
    { field: 'consumedByName', header: 'ConsumedBy',  width: 160,
      text: r => r.consumedByName ?? '', render: r => r.consumedByName ?? '' },
    { field: 'comment',        header: 'Comment',     width: 200,
      edit: { type: 'text' as const },
      text: r => r.comment ?? '', render: r => r.comment ?? '' },
  ];

  /** The unopened amount for this container — legacy's
   *  Convert.ToInt32(Item.Amount) fallback when there are no depletions yet. */
  const startAmount = Number(selectedRow?.amount ?? '') || 0;

  /** Recompute the whole chain the way legacy does, in Sort order:
   *    remain(0)           = Item.Amount
   *    row(i).stockAmount  = remain(i-1)
   *    row(i).amountRemain = remain(i-1) - row(i).amountTaken
   *  The SERVER recomputes this too and is authoritative; doing it here is what
   *  makes the grid update as you type, exactly as legacy's ObjectChanged does. */
  const rechain = useCallback((list: DepEdit[]): DepEdit[] => {
    let remain = startAmount;
    return list.map((d, i) => {
      const row = { ...d, sort: i + 1, stockAmount: remain, amountRemain: remain - d.amountTaken };
      remain = row.amountRemain;
      return row;
    });
  }, [startAmount]);

  /** What the grid shows: the chain runs oldest -> newest, legacy displays it
   *  newest -> oldest. */
  const depletionsNewestFirst = useMemo(
    () => [...depletions].slice().reverse(), [depletions]);

  /** "New" — legacy NewAction_Executing (~590): seed from what is LEFT, take 1,
   *  and add it to the GRID ONLY. When nothing is left it refuses with legacy's
   *  own message instead of adding an impossible row. */
  function addDepletionRow() {
    if (!selectedRow) return;
    const left = depletions.length > 0
      ? Math.min(...depletions.map(d => d.amountRemain))
      : startAmount;
    if (left <= 0) { notify.warning('Depleted item cannot be consumed.'); return; }

    const draft: DepEdit = {
      oid: `draft-${++draftSeq.current}`,
      draft: true,
      amountTaken: 1,
      stockAmount: left,
      amountRemain: left - 1,
      consumedByName: null,
      date: new Date().toISOString(),
      comment: null,
      sort: depletions.length + 1,
    };
    setDepletions(prev => rechain([...prev, draft]));
    setDepDirty(true);
  }

  /** Editing AmountTaken — legacy's two rules (ObjectChanged ~505), including
   *  the REVERT on an over-take rather than silently clamping. */
  function commitDepCell(row: DepEdit, col: AppColumn<DepEdit>, value: string) {
    if (col.field === 'comment') {
      setDepletions(prev => prev.map(d => (d.oid === row.oid ? { ...d, comment: value } : d)));
      setDepDirty(true);
      return;
    }
    if (col.field !== 'amountTaken') return;

    const taken = Number(value);
    if (!Number.isFinite(taken) || taken <= 0) {
      notify.error('Please enter valid amount to consume.');
      return;                                   // legacy leaves the old value
    }
    // What this row may take: everything left BEFORE it in the chain.
    const idx = depletions.findIndex(d => d.oid === row.oid);
    const before = depletions.slice(0, idx);
    const available = before.at(-1)?.amountRemain ?? startAmount;
    if (taken > available) {
      notify.error('Please enter the amount to be taken within the remaining amount.');
      return;                                   // legacy reverts to the old value
    }
    setDepletions(prev => rechain(prev.map(d => (d.oid === row.oid ? { ...d, amountTaken: taken } : d))));
    setDepDirty(true);
  }

  /** Remove ticked rows from the GRID. Nothing is written until Save, which is
   *  how legacy's child grid behaves. */
  async function removeDepletionRows() {
    if (selectedDep.size === 0) return;
    if (!(await confirm({
      title: 'Remove',
      message: `Remove ${selectedDep.size} depletion(s) from this item?`,
      tone: 'danger', confirmLabel: 'Remove',
    }))) return;
    setDepletions(prev => rechain(prev.filter(d => !selectedDep.has(d.oid))));
    setSelectedDep(new Set());
    setDepDirty(true);
  }

  /** SAVE — legacy SaveAction_Executing (~355). Posts the whole SET; the SP
   *  recomputes the chain, re-applies both rules and, when the container ends
   *  empty, drops Items.StockQty by one and closes the LT as Consumed. */
  async function saveDepletions() {
    if (!selectedRow) return;
    setSaving(true);
    try {
      await api.put(`/api/v1/fractional-consumptions/${selectedRow.oid}/depletions`, {
        rows: depletions.map(d => ({
          oid: d.draft ? '' : d.oid,
          amountTaken: d.amountTaken,
          comment: d.comment,
        })),
      });
      await loadDepletions(selectedRow.oid);
      await load();
      notify.success('Saved successfully.');
    } catch (err) {
      // The SP owns the real rules; show what it said.
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {history ? (
              <>
                <span onClick={() => { setHistory(false); setSelectedRow(null); }}
                      style={{ cursor: 'pointer' }}>Fractional Consumption</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">Fractional Consumption History</span>
              </>
            ) : (
              <span className="dp-bc-active">Fractional Consumption</span>
            )}
          </div>
          <div className="dp-title-row">
            <FormTitle
              pageId="icm-fractional"
              title={history ? 'Fractional Consumption History' : 'Fractional Consumption'}
              loadMs={loadMs}
              loading={loading}
            />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              /* HISTORY — legacy's FractionalHistory action
                 (DistributionViewController ~75), targeted at the pending list
                 ONLY; the view it opens has no way back on its bar, so the
                 breadcrumb is it, as on the other ICM forms. */
              !history ? (
                <button className="ca-btn" onClick={() => { setHistory(true); setSelectedRow(null); }}
                  title="Show every fractional record">
                  <i className="ti ti-history" /> History
                </button>
              ) : undefined
            }
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: history ? 'fractional-consumption-history' : 'fractional-consumptions', ...gridExportMatrix(filtered, mainCols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search LT, item…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<FractionalRow>
              rows={filtered}
              columns={mainCols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              onRowClick={selectRow}
              emptyText={searchGate
                ? 'Search for an LT# or item to begin.'
                : 'No fractional items pending depletion.'}
            />
          )}
        </div>

        {/* Depletion detail panel */}
        {selectedRow && (
          <div className="bl-card" style={{ marginTop: 16, padding: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>
              Depletions — {selectedRow.itemName} ({selectedRow.lt})
            </div>
            {/* Legacy's child bar is New + Save (the grid buffers; Save commits
                the set). Remove is AI's equivalent of dropping a buffered row —
                it too writes nothing until Save. */}
            <AppToolbar
              left={
                <>
                  <button className="ca-btn" onClick={addDepletionRow}>
                    <i className="ti ti-plus" /> New
                  </button>
                  <button className="ca-btn" style={{ marginLeft: 6 }}
                    onClick={saveDepletions} disabled={saving || !depDirty}>
                    <BusyIcon busy={saving} icon="ti-device-floppy" /> Save
                  </button>
                </>
              }
              onDelete={removeDepletionRows}
              deleteDisabled={selectedDep.size === 0}
              onRefresh={() => loadDepletions(selectedRow.oid)}
              refreshing={depLoading}
            />
            <div style={{ marginTop: 8 }}>
              <AppDataGrid<DepEdit>
                /* NEWEST FIRST. Legacy's child grid is sorted Date DESCENDING
                   with NewItemRowPosition="Top"
                   (Distribution_itemDepletionsCollection_ListView), so the take
                   you just added sits at the top. The CHAIN is still computed in
                   `sort` order -- only the display is reversed, and the editor
                   commits by oid, so nothing else has to change. */
                rows={depletionsNewestFirst}
                columns={depCols}
                getRowId={r => r.oid}
                loading={depLoading}
                loadMs={null}
                selectable
                selectedIds={selectedDep}
                onSelectionChange={setSelectedDep}
                onCellCommit={commitDepCell}
                emptyText="No depletion records yet."
              />
            </div>
          </div>
        )}
      </div>

      {/* Add depletion dialog */}
    </div>
  );
}
